<template>
  <input
    ref="input"
    v-model="model"
    data-test="single-input"
    :type="inputTypeValue"
    :placeholder="placeholder"
    :disabled="isDisabled"
    min="0"
    max="9"
    maxlength="1"
    pattern="[0-9]"
    :class="[inputClasses ? inputClasses : 'otp-input', conditionalClass, { 'is-complete': model }]"
    @input="handleOnChange"
    @keydown="handleOnKeyDown"
    @paste="handleOnPaste"
    @focus="handleOnFocus"
    @blur="handleOnBlur"
  />

  <div v-if="isSeperate" class="flex flex-col items-center justify-center">
    {{ separator }}
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref, Ref, watch } from "vue";

// conmponent heavily based on Vue3OtpInput
export default defineComponent({
  name: "SingleOTPInput",
  props: {
    inputType: {
      type: String as PropType<"number" | "tel" | "letter-numeric" | "password">,
      validator: (value: string) => ["number", "tel", "letter-numeric", "password"].includes(value),
      default: "tel",
    },

    inputmode: {
      type: String,
      default: "numeric",
    },

    value: {
      type: [String, Number],
    },

    separator: {
      type: String,
    },

    focus: {
      type: Boolean,
    },

    inputClasses: {
      type: [String, Array] as PropType<string[] | string>,
    },

    conditionalClass: {
      type: String,
    },

    shouldAutoFocus: {
      type: Boolean,
    },

    isLastChild: {
      type: Boolean,
    },

    placeholder: {
      type: String,
    },

    isDisabled: {
      type: Boolean,
    },

    upperCase: {
      type: Boolean,
    },

    seperatorEveryN: {
      type: Number,
      default: 0,
    },

    inputNumber: {
      type: Number,
      default: 1,
    },
  },

  emits: ["on-change", "on-keydown", "on-paste", "on-focus", "on-blur"],
  setup(props, { emit }) {
    const model = ref(props.value || "");
    const input = ref<HTMLInputElement | null>(null) as Ref<HTMLInputElement>;
    const isSeperate = ref(false);
    if (
      !props.isLastChild &&
      props.seperatorEveryN &&
      props.separator &&
      props.inputNumber % props.seperatorEveryN === 0
    ) {
      isSeperate.value = true;
    }
    const handleOnChange = () => {
      model.value = model.value.toString();
      if (props.upperCase) {
        model.value = model.value.toUpperCase();
      }
      if (model.value.length > 1) {
        model.value = model.value.slice(0, 1);
      }
      return emit("on-change", model.value);
    };
    const isCodeLetter = (charCode: number) => charCode >= 65 && charCode <= 90;
    const isCodeNumeric = (charCode: number) =>
      (charCode >= 48 && charCode <= 57) || (charCode >= 96 && charCode <= 105);
    // numeric keys and numpad keys
    const handleOnKeyDown = (event: KeyboardEvent) => {
      if (props.isDisabled) {
        event.preventDefault();
      }
      // Only allow characters 0-9, DEL, Backspace, Enter, Right and Left Arrows, and Pasting
      const keyEvent = event || window.event;
      const charCode = keyEvent.which ? keyEvent.which : keyEvent.keyCode;
      if (
        isCodeNumeric(charCode) ||
        (props.inputType === "letter-numeric" && isCodeLetter(charCode)) ||
        [8, 9, 13, 37, 39, 46, 86].includes(charCode)
      ) {
        emit("on-keydown", event);
      } else {
        keyEvent.preventDefault();
      }
    };
    const handleOnPaste = (event: ClipboardEvent) => emit("on-paste", event);
    const handleOnFocus = () => {
      input.value.select();
      return emit("on-focus");
    };
    const handleOnBlur = () => emit("on-blur");
    watch(
      () => props.value,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          model.value = newValue as string | number;
        }
      }
    );
    watch(
      () => props.focus,
      (newFocusValue, oldFocusValue) => {
        // Check if focusedInput changed
        // Prevent calling function if input already in focus
        if (oldFocusValue !== newFocusValue && input.value && props.focus) {
          input.value.focus();
          input.value.select();
        }
      }
    );
    onMounted(() => {
      if (input.value && props.focus && props.shouldAutoFocus) {
        input.value.focus();
        input.value.select();
      }
    });
    return {
      handleOnChange,
      handleOnKeyDown,
      handleOnPaste,
      handleOnFocus,
      handleOnBlur,
      input,
      model,
      inputTypeValue: props.inputType === "letter-numeric" ? "text" : props.inputType,
      isSeperate,
    };
  },
});
</script>

<style scoped lang="scss">
.otp-input {
  width: 100%;
  aspect-ratio: 1 / 1;
  font-size: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #000000;
  text-align: center;
  flex: 1;
}
.otp-input:active,
.otp-input:focus-visible,
.otp-input:focus {
  border-color: #ff4a65 !important;
  outline: none;
}
</style>
